import {
  Flex,
  IconButton,
  IconTrashLine,
  IconUploadLine,
  IconWarningLine,
  Spinner,
  Text,
  Tooltip,
} from "@instructure/ui";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { ExternalService } from "../../../services/ExternalService";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";
import UploadFileModal from "./UploadFileModal";
import { sortByLastName } from "./utils";

const Student = ({
  student,
  setRoster,
  raterData,
  setRaterData,
  contextId,
  shareId,
  assessmentId,
  setRefreshData,
}) => {
  const showNotification = useNotification();
  const [openEvidenceModal, setOpenEvidenceModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [scoreFinalized, setScoreFinalized] = useState(false);

  useEffect(() => {
    Object.keys(raterData).forEach((student) => {
      Object.keys(raterData[student]).forEach((outcome) => {
        if (raterData[student][outcome].score_finalized) {
          setScoreFinalized(true);
        }
      });
    });
  }, [raterData]);

  const handleSubmitEvidenceFiles = async (evidenceFiles) => {
    await ExternalService.saveFiles(
      evidenceFiles,
      "evidence",
      assessmentId + "/" + student.canvas_id.toString(),
      "evidence.zip"
    )
      .then(() => {
        setOpenEvidenceModal(false);
        setRefreshData(true);
        showNotification(t("juriedAssessment.uploadEvidenceSuccess"));
      })
      .catch(() => {
        showNotification(t("notifications.general.uploadError"), ERROR);
      });
  };

  const handleRemoveStudent = () => {
    setLoading(true);
    ExternalService.removeExternalUserFromOffering(
      student.ddl_id,
      contextId
    ).then(() => {
      setRoster((prevRoster) =>
        prevRoster
          .filter((s) => s.ddl_id !== student.ddl_id)
          .sort(sortByLastName)
      );
      setRaterData((prevData) => {
        const newData = { ...prevData };
        delete newData[student.canvas_id];
        window.localStorage.setItem(
          `raterData/${shareId}`,
          JSON.stringify(newData)
        );
        return newData;
      });
      setLoading(false);
      setRefreshData(true);
    });
  };

  const handleNameDisplay = (student) => {
    if (student.name) {
      return student.name;
    } else if (student.email) {
      return student.email;
    } else {
      return (
        <Flex gap="xx-small">
          <Flex.Item>{student.canvas_id.toString()}</Flex.Item>
          <Flex.Item margin="0 0 xxx-small 0">
            <Tooltip renderTip={t("rater.noNameOrEmail")}>
              <IconWarningLine color="warning" />
            </Tooltip>
          </Flex.Item>
        </Flex>
      );
    }
  };

  return (
    <>
      <Flex alignItems="center" justifyItems="space-between">
        <Flex.Item>{handleNameDisplay(student)}</Flex.Item>
        <Flex.Item>
          <Tooltip
            color="primary-inverse"
            renderTip={t("rater.uploadEvidence")}
          >
            <IconButton
              screenReaderLabel={t("rater.uploadEvidence")}
              renderIcon={IconUploadLine}
              size="small"
              onClick={() => setOpenEvidenceModal(true)}
            />
          </Tooltip>
          {!scoreFinalized && (
            <Tooltip
              color="primary-inverse"
              renderTip={t("rater.removeStudent")}
            >
              <IconButton
                screenReaderLabel={t("rater.removeStudent")}
                renderIcon={
                  loading ? (
                    <Spinner
                      renderTitle={t("global.pleaseWait")}
                      size="x-small"
                    />
                  ) : (
                    IconTrashLine
                  )
                }
                disabled={loading}
                size="small"
                margin="0 0 0 small"
                onClick={handleRemoveStudent}
              />
            </Tooltip>
          )}
        </Flex.Item>
      </Flex>
      <UploadFileModal
        shouldOpen={openEvidenceModal}
        modalHeaderText={t("rater.uploadEvidenceFor") + student.name}
        fileDropHeader={t("juriedAssessment.fileDrop")}
        handleCancel={() => setOpenEvidenceModal(false)}
        handleSubmit={handleSubmitEvidenceFiles}
        multiFileSupport={true}
        fileExtensions={
          ".csv,.txt,.xls,.xlsx,.pdf,.doc,.docx,.jpg,.jpeg,.png,.zip"
        }
        modalCopy={
          <Text as="div" lineHeight="double">
            {t("juriedAssessment.uploadInstructions")}
          </Text>
        }
      />
    </>
  );
};

export default Student;
